import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { DotSpinner } from '@crono-one/design-system';
import { toast } from '@crono-one/design-system/side-effects';

import PATH from 'crono-fe-common/constants/path';
import useZohoAuthCallback from 'hooks/services/zoho/useZohoAuthCallback';
import {
  clearZohoOauthState,
  isZohoOauthStateValid,
} from 'hooks/services/zoho/zohoOauthState';
import { useAuth } from 'context/auth';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import useOauthCallbackCode from 'pages/connect/views/useOauthCallbackCode';
import storeOnboardingError from 'pages/connect/views/storeOnboardingError';

const ZohoLoginHandler: FC = () => {
  const { refreshUser } = useAuth();
  const { search } = useLocation();
  const { mutate: zohoAuthCallback } = useZohoAuthCallback();

  const accountsServer = queryString.parse(search)['accounts-server'];

  useOauthCallbackCode({
    provider: 'Zoho',
    exchangeCode: zohoAuthCallback,
    buildVariables: (code) => ({
      code,
      accountsServer:
        typeof accountsServer === 'string' ? accountsServer : null,
    }),
    missingCodePath: PATH.CONNECT,
    successPath: PATH.SETTINGS.INTEGRATIONS.MAIN,
    errorPath: PATH.CONNECT,
    // A callback that fails the state check leaves the nonce alone, or a forged
    // redirect would invalidate the legitimate flow still pending in this tab.
    guard: ({ state }) => {
      if (isZohoOauthStateValid(state)) return true;

      toast.error('Zoho returned an unexpected state');
      return false;
    },
    onSuccess: () => {
      clearZohoOauthState();
      return refreshUser();
    },
    onError: (error) => {
      clearZohoOauthState();
      storeOnboardingError(error, IntegrationType.ZOHO);
    },
  });

  return (
    <div className="flex h-full w-full items-center justify-center">
      <DotSpinner />
    </div>
  );
};

export default ZohoLoginHandler;
